import React, { Children, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { css } from '@linaria/core'
import { sanitizeProps, useRandomId } from '@fs/zion-frontend-friends'
import List from './List'
import Checkbox from '../checkbox'
import Divider from '../divider'
import { colors } from '../theme'
import LayoutBand from '../layout-band'
import useCheckboxList from './use-checkbox-list/useCheckboxList'

// Make the Select All checkbox match the keyline of ListItems
const selectAllCss = css`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  & label {
    padding: 0 24px !important;
  }
`

const stickyCss = css`
  position: sticky;
  z-index: 1; /* position: sticky goes above some things, but not above position:relative or things with z-index. */
  top: 0;
  background: ${colors.gray00};
`

const actionBtnCss = css`
  display: flex;
  margin-top: -4px;
  & > *:not(:first-of-type) {
    margin-inline-start: 8px;
  }
`

const CheckboxList = forwardRef(
  (
    {
      children,
      selectAllLabel,
      selectionLimit = Infinity,
      onSelectionChange,
      ghosted,
      initialAllSelected = false,
      initialSelection,
      primaryActionIcon,
      secondaryActionIcon,
      ...props
    },
    ref
  ) => {
    const { getPropsForCheckbox, selectAllCheckbox } = useCheckboxList({
      onSelectionChange,
      selectAllLabel,
      selectionLimit,
      initialAllSelected,
      initialSelection,
      children,
    })

    return (
      <div {...sanitizeProps(props)}>
        {selectAllLabel && (
          <LayoutBand className={stickyCss}>
            <div className={selectAllCss}>
              {selectAllCheckbox}
              <div className={actionBtnCss}>
                {secondaryActionIcon}
                {primaryActionIcon}
              </div>
            </div>
            <Divider />
          </LayoutBand>
        )}
        {/* Ignore ghosted if initialSelection is provided (ZIONUIV13-181) */}
        <List ghosted={initialSelection ? false : ghosted} ref={ref}>
          {Children.map(children, (child) => (
            <CloneWithCheckbox child={child} getPropsForCheckbox={getPropsForCheckbox} />
          ))}
        </List>
      </div>
    )
  }
)

// This has to be it's own component because we to be able to use a hook to set the checkbox id
function CloneWithCheckbox({ child, getPropsForCheckbox }) {
  const checkboxLabelId = useRandomId()
  const Tag = child.type
  const { name, primaryText, disabled } = child.props
  const effectiveName = name || primaryText
  return (
    <Tag
      ref={child.ref}
      {...child.props}
      textId={checkboxLabelId}
      startElement={
        <Checkbox
          aria-labelledby={checkboxLabelId}
          {...getPropsForCheckbox.current({ name: effectiveName, disabled })}
        />
      }
    />
  )
}

CheckboxList.propTypes = {
  /** The maximum number of checkboxes that can be checked. If none is provided then there is no limit. */
  selectionLimit: PropTypes.number,

  /** Label text for the Select All checkbox. If this is not supplied, there will be no "Select All" type feature. */
  selectAllLabel: PropTypes.string,

  /** Callback for when the selection state of one or more of the checkboxes changes. Returns Array of selected ListItem names.
   *  Wrap the function you provide in a useCallback.
   */
  onSelectionChange: PropTypes.func,

  /** 👻 If you have a large List, you gonna wanna set dis...trus' me. */
  ghosted: PropTypes.bool,

  /** Set this to TRUE if you want all the checkboxes to be initially selected on the List */
  initialAllSelected: PropTypes.bool,

  /** Names of checkbox list items that should start out in a checked state */
  initialSelection: PropTypes.arrayOf(PropTypes.string),

  /** Furthest the end IconButton. Intended to apply an action across all the selected ListItems */
  primaryActionIcon: PropTypes.element,

  /** Second from the end IconButton. Intended to apply an additional action across all the selected ListItems */
  secondaryActionIcon: PropTypes.element,
}

export default CheckboxList
